import type { DateTime } from "luxon";
import type { Department } from "../../../contexts/AuthProvider";
import { getJobDisplayName } from "../../../utils/roles";
import type { ScheduleRow } from "../types";
import { resolveRoleShortCode } from "../util/roleHelpers";
import { getRowScheduledHours } from "../util/scheduledHours";

/**
 * A row projected into the shape used by **row sort** (mapping §5.2, §5.3).
 *
 * One field per `PrintoutColumnType`. The value is whatever `compareValues` (mapping §5.3) sees
 * for that column kind. No other module in `sort/` switches on column kind — sort always goes
 * through this projection.
 *
 * **Intentional twin elsewhere:** `render/mergeColumnSort.ts::getMergeColumnKey` is a parallel
 * projection used for **vertical-merge equality** (and `staffPerRow` batch boundaries). It is
 * deliberately different from this one:
 * - `role` under JobManagement → `jobId` (stable id, so two jobs with the same display name don't
 *   accidentally merge) vs. **here** → resolved display name (what the user sees, so sort lands
 *   alphabetically)
 * - `shiftTime` → `${start.toISO()}~${end.toISO()}` string (deterministic equality) vs. **here**
 *   → the raw `DateTime` (so compareValues step 5 fires)
 * - `certification` → raw role code (stable identity) vs. **here** → short code (user-facing)
 *
 * Sort needs user-visible ordering; merge needs stable identity. Keep them separate; do not
 * unify them into a single switch.
 *
 * Missing-shift rows (mapping §5.5) get empty strings for `name` / `initials` / `notes`, which is
 * exactly the trigger for `compareValues` step 6 (empty after non-empty) — no special-case pinning
 * is needed in the sort layer.
 */
export interface SortProjection {
	name: string;
	initials: string;
	location: string;
	role: string;
	certification: string;
	shiftTime: DateTime;
	shiftEnd: DateTime;
	notes: string;
	floor: string;
	scheduledHours: number | undefined;
}

interface ProjectForSortArgs {
	row: ScheduleRow;
	jobManagementEnabled: boolean;
	department: Department;
}

/**
 * Map a `ScheduleRow` to its sort-value projection.
 *
 * Per mapping §5.2: the column labeled **Role** off / **Job** on uses different fields under the
 * feature flag. Per mapping §5.5: missing-shift rows carry `""` for staff-derived fields so
 * `compareValues` step 6 pushes them to the bottom on asc / top on desc.
 */
export const projectForSort = ({ row, jobManagementEnabled, department }: ProjectForSortArgs): SortProjection => {
	const nurseName = row.nurse?.name ?? "";
	const roleShort = resolveRoleShortCode(row.nurse?.role, row.role);

	// Role/Job duality: under JobManagement, sort by the resolved job display name; otherwise by role short code.
	// Empty-string for missing-shift rows under JobManagement-on (no jobId to resolve) — pre-existing behavior.
	const roleSortKey = jobManagementEnabled ? (row.nurse?.jobId ? getJobDisplayName(row.nurse.jobId, department) : "") : roleShort;

	return {
		name: nurseName,
		initials: nurseName,
		location: row.location,
		role: roleSortKey,
		certification: roleShort,
		shiftTime: row.shiftStart,
		shiftEnd: row.shiftEnd,
		notes: row.nurse?.notes ?? "",
		floor: row.floor ?? "",
		scheduledHours: getRowScheduledHours({ row, department, jobManagementEnabled }),
	};
};
